import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWizardState } from "../hooks/useWizardState";
import { Listbox } from "../components/shared/form/Listbox";
import { Input } from "../components/ui/Form/Input";
import "./Step3Style.css";

interface TravelPurpose {
  id: number;
  name: string;
  value: string;
}

const getTravelPurposes = (t: (key: string) => string): TravelPurpose[] => [
  { id: 1, name: t("options.travelPurposes.summerHolidays"), value: "summer-holidays" },
  { id: 2, name: t("options.travelPurposes.natureAdventure"), value: "nature-adventure" },
  { id: 3, name: t("options.travelPurposes.romanticEscape"), value: "romantic-escape" },
  { id: 4, name: t("options.travelPurposes.culturalHistorical"), value: "cultural-historical" },
];

interface PackageOption {
  id: number;
  name: string;
  value: string;
}

const getPackageOptions = (t: (key: string) => string): PackageOption[] => [
  { id: 1, name: t("options.packageOptions.hotelAccommodation"), value: "hotel-accommodation" },
  { id: 2, name: t("options.packageOptions.flights"), value: "flights" },
  { id: 3, name: t("options.packageOptions.otherTransportations"), value: "other-transportations" },
  { id: 4, name: t("options.packageOptions.transfers"), value: "transfers" },
  { id: 5, name: t("options.packageOptions.toursExcursions"), value: "tours-excursions-experiences" },
];

interface BoardBasis {
  id: number;
  name: string;
  value: string;
}

const getBoardBasisOptions = (t: (key: string) => string): BoardBasis[] => [
  { id: 1, name: t("options.boardBasis.allInclusive"), value: "all-inclusive" },
  { id: 2, name: t("options.boardBasis.fullBoard"), value: "full-board" },
  { id: 3, name: t("options.boardBasis.halfBoard"), value: "half-board" },
  { id: 4, name: t("options.boardBasis.breakfast"), value: "breakfast" },
  { id: 5, name: t("options.boardBasis.selfCatering"), value: "self-catering" },
];

export const Step3Style: React.FC = () => {
  const { t } = useTranslation();
  const { formData, updateFormData, errors, setFieldError } = useWizardState();

  const TRAVEL_PURPOSES = useMemo(() => getTravelPurposes(t), [t]);
  const PACKAGE_OPTIONS = useMemo(() => getPackageOptions(t), [t]);
  const BOARD_BASIS_OPTIONS = useMemo(() => getBoardBasisOptions(t), [t]);

  // Find the selected purpose object based on formData.travelPurpose
  const selectedPurpose = useMemo(() => {
    if (!formData.travelPurpose) return null;
    return TRAVEL_PURPOSES.find((purpose) => purpose.value === formData.travelPurpose) || null;
  }, [formData.travelPurpose, TRAVEL_PURPOSES]);

  const handlePurposeChange = (purpose: TravelPurpose | null) => {
    if (purpose) {
      updateFormData({ travelPurpose: purpose.value });
      setFieldError("travelPurpose", undefined);
    } else {
      updateFormData({ travelPurpose: "" });
    }
  };

  // Find the selected package options based on formData.packageOptions
  const selectedPackageOptions = useMemo(() => {
    if (!formData.packageOptions || formData.packageOptions.length === 0) return [];
    return PACKAGE_OPTIONS.filter((option) => 
      formData.packageOptions.includes(option.value)
    );
  }, [formData.packageOptions]);

  const handlePackageOptionsChange = (options: PackageOption[]) => {
    const values = options.map((option) => option.value);
    updateFormData({ 
      packageOptions: values,
      toursDescription: values.includes("tours-excursions-experiences") ? formData.toursDescription : ""
    });
    setFieldError("packageOptions", undefined);
  };

  const showToursDescription = formData.packageOptions?.includes("tours-excursions-experiences") || false;

  // Find the selected board basis object based on formData.boardBasis
  const selectedBoardBasis = useMemo(() => {
    if (!formData.boardBasis) return null;
    return BOARD_BASIS_OPTIONS.find((basis) => basis.value === formData.boardBasis) || null;
  }, [formData.boardBasis, BOARD_BASIS_OPTIONS]);

  const handleBoardBasisChange = (basis: BoardBasis | null) => {
    if (basis) {
      updateFormData({ boardBasis: basis.value });
      setFieldError("boardBasis", undefined);
    } else {
      updateFormData({ boardBasis: "" });
    }
  };

  return (
    <div className="step3-style">
      <h2 className="step-heading">{t("step3.heading")}</h2>

      <div className="form-group">
        <Listbox
          data={TRAVEL_PURPOSES}
          value={selectedPurpose}
          placeholder={t("step3.travelPurpose.placeholder")}
          onChange={handlePurposeChange}
          label={t("step3.travelPurpose.label")}
          displayField="name"
          error={errors.travelPurpose}
          required
        />

        <div className="form-group">
          <Listbox
            data={PACKAGE_OPTIONS}
            value={selectedPackageOptions}
            onChange={handlePackageOptionsChange}
            displayField="name"
            placeholder={t("step3.packageOptions.placeholder")}
            label={t("step3.packageOptions.label")}
            multiple
            error={errors.packageOptions}
            required
          />
        </div>

        {showToursDescription && (
          <div className="conditional-field animate-slide-down">
            <Input
              id="toursDescription"
              label={t("step3.toursDescription.label")}
              placeholder={t("step3.toursDescription.placeholder")}
              value={formData.toursDescription || ""}
              onChange={(e) => {
                updateFormData({ toursDescription: e.target.value });
                setFieldError("toursDescription", undefined);
              }}
              error={errors.toursDescription}
            />
          </div>
        )}

        <div className="form-group">
          <Listbox
            data={BOARD_BASIS_OPTIONS}
            value={selectedBoardBasis}
            placeholder={t("step3.boardBasis.placeholder")}
            onChange={handleBoardBasisChange}
            label={t("step3.boardBasis.label")}
            displayField="name"
            error={errors.boardBasis}
            required
          />
        </div>
      </div>
    </div>
  );
};

